import useIsNA from "@dana/Common/Hooks/useIsNA";

import { Cart, danaBatchOrderPriceCheck } from "@dana/Overrides/Services/CartService";
import placeOrder from "@dana/Overrides/Store/Pages/OnePageCheckout/Handlers/PlaceOrder";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import setShipComplete from "@dana/Store/Pages/OnePageCheckoutExt/Handlers/SetShipComplete";
import OrderProcessSteps, { PriceChangeModalStyles } from "@dana/Components/OnePageCheckout/OrderProcessSteps";
import usePromotionCodeForm from "@insite/client-framework/Common/Hooks/usePromotionCodeForm";
import { SpreedlyIframeStyles, useSpreedlyFrame } from "@insite/client-framework/Common/Hooks/useSpreedlyFrame";
import { IFrame, TokenExIframeStyles, useTokenExFrame } from "@insite/client-framework/Common/Hooks/useTokenExFrame";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadSpreedlyConfig from "@insite/client-framework/Store/Context/Handlers/LoadSpreedlyConfig";
import loadTokenExConfig from "@insite/client-framework/Store/Context/Handlers/LoadTokenExConfig";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import {
    canApplyPromotionsToCart,
    canPlaceOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import resetCurrentCartId from "@insite/client-framework/Store/Data/Carts/Handlers/ResetCurrentCartId";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getCurrentPromotionsDataView,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import applyPromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ApplyPromotion";
import removePromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/RemovePromotion";
import setIsPlacingOrder from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsPlacingOrder";
import setOnePageCheckoutPaymentMethod from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetOnePageCheckoutPaymentMethod";
import setPaymentDetailsState from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetPaymentDetailsState";
import preloadOrderConfirmationData from "@insite/client-framework/Store/Pages/OrderConfirmation/Handlers/PreloadOrderConfirmationData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, CartModel, PaymentMethodDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import usePaymentDetailsCartState from "@insite/content-library/Hooks/Payments/usePaymentDetailsCartState";
import usePaymentDetailsFormHandlers from "@dana/Overrides/Hooks/Payments/usePaymentDetailsFormHandlers";
import usePaymentDetailsFormState from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormState";
import usePaymentDetailsFormValidation from "@dana/Overrides/Hooks/Payments/usePaymentDetailsFormValidation";
import CreditCardDetailsEntry, {
    CreditCardDetailsEntryStyles,
    CreditCardDetailsValidation,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CreditCardDetailsEntry";
import ECheckDetailsEntry, {
    ECheckDetailsEntryStyles,
    Validatable,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/ECheckDetailsEntry";
import PaymentProfileBillingAddress from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/PaymentProfileBillingAddress";
import SavedPaymentProfileEntry, {
    SavedPaymentProfileEntryStyles,
    SavedPaymentProfileValidation,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/SavedPaymentProfileEntry";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { generateSpreedlyFrameStyleConfig } from "@insite/mobius/SpreedlyFrame";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { generateTokenExFrameStyleConfig } from "@insite/mobius/TokenExFrame";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import setOrderProgressMode from "@dana/Store/Data/OrderProgressLoading/Handlers/OrderProgressLoadingHandler";

const mapStateToProps = (state: ApplicationStateDana) => {
    const { cartId, isPlacingOrder, paymentDetailsState, placeOrderErrorMessage } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const {
        websiteSettings,
        productSettings: { enableVat },
        cartSettings: { bypassCvvForSavedCards },
    } = getSettingsCollection(state);
    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);
    const promotions = promotionsDataView.value;
    const notes = state.onePageCheckoutStateExt.editableNotes;
    const additionalEmails = state.onePageCheckoutStateExt.additionalEmails;
    const carrierAccountNumber = state.onePageCheckoutStateExt.carrierAccountNumber;
    const danaSettings = getDanaGeneralSettings(state);


    return {
        cartState,
        websiteSettings,
        tokenExConfigs: state.context.tokenExConfigs,
        savedPaymentsPageLink: getPageLinkByPageType(state, "SavedPaymentsPage"),
        session: state.context.session,
        signInPageLink: getPageLinkByPageType(state, "SignInPage"),
        location: getLocation(state),
        enableVat,
        bypassCvvForSavedCards,
        isPlacingOrder,
        paymentDetailsState,
        placeOrderErrorMessage,
        confirmationPageLink: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage"),
        canApplyPromosToCart: canApplyPromotionsToCart(cartState?.value),
        promotionErrorMessage: state.pages.checkoutReviewAndSubmit.promotionErrorMessage,
        promotions,
        canPlaceOrder: canPlaceOrder(cartState.value),
        spreedlyConfig: state.context.spreedlyConfig,
        notes,
        additionalEmails,
        carrierAccountNumber,
        isNA: useIsNA(state),
        enableSteppedModal: danaSettings?.enableCheckoutRePriceSteps,
        largeCartItemsThresholdBatchSize: danaSettings ? parseInt(danaSettings?.largeCartItemsThresholdBatchSize, 10) : 0,
        priceChangeModalFlag: state.orderProgressMode.isOn

    };
};

const mapDispatchToProps = {
    loadTokenExConfig,
    preloadOrderConfirmationData,
    resetCurrentCartId,
    setOnePageCheckoutPaymentMethod,
    signOut,
    setPaymentDetailsState,
    placeOrder,
    setIsPlacingOrder,
    applyPromotion,
    removePromotion,
    loadSpreedlyConfig,
    setShipComplete,
    setOrderProgressMode
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasHistory &
    HasToasterContext;

export interface OnePageCheckoutPaymentDetailsStyles {
    form?: InjectableCss;
    fieldset?: InjectableCss;
    paymentDetailsGridContainer?: GridContainerProps;
    paymentDetailsHeadingGridItem?: GridItemProps;
    paymentDetailsHeadingText?: TypographyPresentationProps;
    paymentdMethodInfoGridItem?: GridItemProps;
    paymentdMethodNameText?: TypographyPresentationProps;
    paymentdMethodCreditCardInfoText?: TypographyPresentationProps;
    editGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    paymentMethodAndPONumberContainer?: GridContainerProps;
    paymentMethodGridItem?: GridItemProps;
    paymentMethodSelect?: SelectPresentationProps;
    paymentProfileBillingAddress?: any;
    paymentProfileExpiredErrorWrapper?: InjectableCss;
    paymentProfileExpiredErrorText?: TypographyPresentationProps;
    paymentProfileEditCardLink?: LinkPresentationProps;
    vatNumberGridItem?: GridItemProps;
    vatNumberText?: TextFieldPresentationProps;
    poNumberGridItem?: GridItemProps;
    poNumberText?: TextFieldPresentationProps;
    emptyGridItem?: GridItemProps;
    adyenDropInGridItem?: GridItemProps;
    mainContainer?: GridContainerProps;
    savedPaymentProfile?: SavedPaymentProfileEntryStyles;
    creditCardDetailsGridItem?: GridItemProps;
    eCheckDetailsGridItem?: GridItemProps;
    creditCardDetails?: CreditCardDetailsEntryStyles;
    creditCardAddressGridItem?: GridItemProps;
    eCheckDetailsEntryStyles?: ECheckDetailsEntryStyles;
    saveAndContinueButtonGridItem: GridItemProps;
    saveAndContinueButton?: ButtonPresentationProps;
    saveCardCheckboxGridItem: GridItemProps;
    promotionsCodeGridItem?: GridItemProps;
    promotionTagsGridContainerItem?: GridItemProps;
    promotionTagsGridContainer?: GridContainerProps;
    promotionCodeGridContainer?: GridContainerProps;
    promotionTagsGridItem?: GridItemProps;
    promotionCodeTag?: TagPresentationProps;
    promotionCodeTagName?: TypographyPresentationProps;
    addDiscountGridItem?: GridItemProps;
    addDiscountButton?: ButtonPresentationProps;
    enterPromotionCodeGridItem?: GridItemProps;
    enterPromotionCodeTextField?: TextFieldPresentationProps;
    enterPromotionCodeApplyButton?: ButtonPresentationProps;
    appliedPromoCodeOverviewText?: TypographyPresentationProps;
    discountLabel?: TypographyPresentationProps;
    priceChangeModal?: PriceChangeModalStyles;
}

export const onePageCheckoutPaymentDetailsStyles: OnePageCheckoutPaymentDetailsStyles = {
    fieldset: {
        css: css`
            margin: 0;
            padding: 0;
            border: 0;
        `,
    },
    paymentDetailsGridContainer: { gap: 20 },
    paymentDetailsHeadingGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    paymentDetailsHeadingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    paymentdMethodInfoGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    editGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    paymentMethodAndPONumberContainer: {
        gap: 10,
        css: css`
            margin-bottom: 8px;
        `,
    },
    paymentMethodGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    paymentProfileExpiredErrorWrapper: {
        css: css`
            display: flex;
            width: 100%;
        `,
    },
    paymentProfileExpiredErrorText: { color: "danger" },
    paymentProfileEditCardLink: {
        css: css`
            margin-left: 1rem;
        `,
    },
    vatNumberGridItem: { width: 6 },
    poNumberGridItem: { width: 6 },
    emptyGridItem: { width: 6 },
    creditCardDetailsGridItem: { width: 12 },
    eCheckDetailsGridItem: { width: [12, 12, 12, 6, 6] },
    creditCardAddressGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    adyenDropInGridItem: { width: 12 },
    saveAndContinueButtonGridItem: {
        width: 12,
    },
    saveAndContinueButton: {
        css: css`
            margin-top: 1em;
        `,
    },
    saveCardCheckboxGridItem: {
        width: 12,
    },
    promotionsCodeGridItem: {
        width: 12,
    },
    promotionCodeGridContainer: {
        gap: 10,
    },
    promotionTagsGridContainer: {
        gap: 2,
    },
    promotionTagsGridContainerItem: {
        width: 12,
    },
    promotionTagsGridItem: {
        width: 12,
    },
    addDiscountGridItem: {
        width: 12,
    },
    addDiscountButton: {
        variant: "secondary",
    },
    enterPromotionCodeGridItem: {
        width: 6,
    },
    enterPromotionCodeApplyButton: {
        variant: "secondary",
    },
    promotionCodeTag: {
        css: css`
            & > button {
                min-height: 0;
            }
        `,
    },
    promotionCodeTagName: {
        css: css`
            align-self: flex-end;
            padding-bottom: 15px;
        `,
    },
    appliedPromoCodeOverviewText: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    priceChangeModal: {},
};

let tokenExAccountNumberIframe: IFrame | undefined;
let tokenExFrameStyleConfig: TokenExIframeStyles;
let spreedlyFrameStyleConfig: SpreedlyIframeStyles;

const styles = onePageCheckoutPaymentDetailsStyles;
const StyledForm = getStyledWrapper("form");
const StyledFieldSet = getStyledWrapper("fieldset");

const OnePageCheckoutPaymentDetails = ({
    cartState,
    tokenExConfigs,
    savedPaymentsPageLink,
    history,
    loadTokenExConfig,
    loadSpreedlyConfig,
    theme,
    enableVat,
    bypassCvvForSavedCards,
    setOnePageCheckoutPaymentMethod,
    isPlacingOrder,
    paymentDetailsState,
    placeOrderErrorMessage,
    signInPageLink,
    setPaymentDetailsState,
    placeOrder,
    setIsPlacingOrder,
    confirmationPageLink,
    canApplyPromosToCart,
    applyPromotion,
    promotionErrorMessage,
    promotions,
    removePromotion,
    canPlaceOrder,
    spreedlyConfig,
    notes,
    additionalEmails,
    carrierAccountNumber,
    setShipComplete,
    isNA,
    enableSteppedModal,
    priceChangeModalFlag,
    setOrderProgressMode,
    largeCartItemsThresholdBatchSize
}: Props) => {
    const paymentDetailsFormState = usePaymentDetailsFormState();
    const {
        paymentMethod,
        setPaymentMethod,
        poNumber,
        vatNumber,
        saveCard,
        cardHolderName,
        cardNumber,
        cardType,
        expirationMonth,
        expirationYear,
        securityCode,
        countryId,
        paymentMethodError,
        poNumberError,
        showFormErrors,
        setShowFormErrors,
        isCardNumberTokenized,
        setIsCardNumberTokenized,
        isECheckTokenized,
        setIsECheckTokenized,
        accountHolderName,
        setAccountHolderName,
        accountNumber,
        setAccountNumber,
        routingNumber,
        setRoutingNumber,
        creditCardLastFourDigits,
        bankAccountLastFourDigits,
        isIframeLoaded,
        paymentMethodRef,
        poNumberRef,
        maskedCardNumber,
        setMaskedCardNumber,
    } = paymentDetailsFormState;
    const paymentDetailsCartState = usePaymentDetailsCartState(cartState, paymentMethod, countryId);
    const {
        cart,
        useTokenExGateway,
        useECheckTokenExGateway,
        paymentOptions,
        paymentMethods,
        paymentMethodDto,
        isCreditCard,
        isECheck,
        isPaymentProfile,
        useSpreedlyDropIn,
    } = paymentDetailsCartState;
    const paymentDetailsFormValidation = usePaymentDetailsFormValidation(
        paymentDetailsFormState,
        paymentDetailsCartState,
    );
    const [danaShipComplete, setDanaShipComplete] = useState(false);
    const { validatePaymentMethod, validatePONumber, validatePONumberDuplicate } = paymentDetailsFormValidation;
    const {
        handlePONumberChange,
        handlePONumberBlurChange,
        handleVatNumberChange,
        handleSaveCardChange,
    } = usePaymentDetailsFormHandlers({
        paymentDetailsFormState,
        paymentDetailsFormValidation,
        paymentDetailsCartState,
        history,
        savedPaymentsPageLink,
        setPaymentMethodCallback: (
            event: React.ChangeEvent<HTMLSelectElement>,
            paymentMethods: PaymentMethodDto[] | null | undefined,
        ) => {
            setOnePageCheckoutPaymentMethod({
                paymentMethod: paymentMethods?.find(method => method.name === event.target.value),
            });
        },
    });

    // Order process modal state
    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
    const [pendingOrderNumber, setPendingOrderNumber] = useState<string | null>(null);
    const [pendingOrderCartId, setPendingOrderCartId] = useState<string | null>(null);
    const [pendingIsBatchSubmit, setPendingIsBatchSubmit] = useState(false);
    const originalCartLinesRef = useRef<CartLineModel[] | null>(null);
    const originalOrderSubTotalRef = useRef<number | undefined>(undefined);
    const [isEmergencyOrder, setIsEmergencyOrder] = useState(false);

    useEffect(() => {
        setOrderProgressMode({ isOn: isOrderModalOpen });
    }, [isOrderModalOpen]);

    const tokenExLibraryIsLoaded = useTokenExFrame();
    const spreedlyLibraryIsLoaded = useSpreedlyFrame();
    const {

        promotionCode,
        setPromotionCode,
        promotionsWithCode,
        setRemovingPromotionCode,
        isAddingDiscount,
        setIsAddingDiscount,
        isUpdatingPromotions,
        setIsUpdatingPromotions,
    } = usePromotionCodeForm(promotions);
    const toasterContext = React.useContext(ToasterContext);
    const [isPolling, setIsPolling] = useState(false);
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const stopPolling = () => {
        if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
        if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
        setIsPolling(false);
    };

    const startPolling = (cartId: string) => {
        setIsPolling(true);

        pollIntervalRef.current = setInterval(() => {
            danaBatchOrderPriceCheck(cartId)
                .then(result => {
                    if (result.success) {
                        stopPolling();
                        if (confirmationPageLink) {
                            history.push(`${confirmationPageLink.url}?cartId=${cartId}`);
                        }
                    }
                })
                .catch(() => { /* continue polling on transient errors */ });
        }, 5000);

        // 5-minute overall timeout
        pollTimeoutRef.current = setTimeout(() => {
            stopPolling();
            toasterContext?.addToast({
                body: translate("Order processing timed out. Please try again or contact support."),
                messageType: "danger",
                isFocusAfterMount: true,
            });
        }, 300000);
    };
    useEffect(() => {
        if (!isPlacingOrder || !canPlaceOrder) {
            return;
        }

        const runValidationAndPlaceOrder = async () => {
            const isValid = await validateForm();
            if (!isValid) {
                setShowFormErrors(true);
                setIsPlacingOrder({ isPlacingOrder: false });
                return;
            }

            // Capture original cart lines before placing order for price comparison
            originalCartLinesRef.current = cart?.cartLines ?? null;
            originalOrderSubTotalRef.current = cart?.orderSubTotal;

        // Open the modal immediately on Place Order click — before the API responds
        if (enableSteppedModal) {
            setIsOrderModalOpen(true);
        }
        const lineCount = cart?.cartLines?.length ?? 0;
        const threshold = largeCartItemsThresholdBatchSize;
        const isLargeCart = threshold && !Number.isNaN(threshold) && lineCount >= threshold;

        const placeOrderProps = {
            paymentMethod,
            poNumber,
            vatNumber,
            saveCard,
            cardHolderName,
            cardNumber,
            cardType,
            expirationMonth,
            expirationYear,
            securityCode,
            useBillingAddress: true,
            address1: "",
            countryId: "",
            stateId: "",
            city: "",
            postalCode: "",
            accountHolderName,
            accountNumber,
            routingNumber,
            payerId: "",
            maskedCardNumber,
            properties: {
                notes,
                additionalEmails,
                carrierAccountNumber,
                "shipComplete": danaShipComplete,
            },
            onSuccess: (resultCart: Cart) => {
                setIsPlacingOrder({ isPlacingOrder: false });

                if (!enableSteppedModal && isLargeCart) {
                    const orderId = (resultCart as any).id ?? resultCart?.id;
                    startPolling(orderId);

                } else if (!enableSteppedModal && !isLargeCart) {
                    if (confirmationPageLink) {
                        history.push(`${confirmationPageLink.url}?cartId=${resultCart?.id}`);
                    }
                    return;
                }
                // Stepped modal: open it; polls will drive the rest
                const isBatch = (resultCart as any).collectAccountNumber === "BatchSubmit";
                setPendingOrderNumber((resultCart as any).orderNumber ?? null);
                setPendingOrderCartId(resultCart.id ?? null);
                setPendingIsBatchSubmit(isBatch);
                setIsOrderModalOpen(true);
            },
            onError: () => {
                setIsCardNumberTokenized(false);
                setIsPlacingOrder({ isPlacingOrder: false });
                setIsOrderModalOpen(false);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.cart) {
                    const returnedCart = resultProps.apiResult.cart as Cart;
                    setIsPlacingOrder({ isPlacingOrder: false });

                    if (!enableSteppedModal && isLargeCart) {
                        const orderId = (returnedCart as any).id ?? returnedCart.id;
                        startPolling(orderId);

                    } else if (!enableSteppedModal && !isLargeCart) {
                        if (confirmationPageLink) {
                            history.push(`${confirmationPageLink.url}?cartId=${returnedCart.id}`);
                        }
                        return;
                    }
                    const isBatch = (returnedCart as any).collectAccountNumber === "BatchSubmit";
                    setPendingOrderNumber((returnedCart as any).orderNumber ?? null);
                    setPendingOrderCartId(returnedCart.id ?? null);
                    setPendingIsBatchSubmit(isBatch);
                    setIsOrderModalOpen(true);
                } else {
                    this.onError?.();
                }
            },
        };

            placeOrder(placeOrderProps as any);
        };

        runValidationAndPlaceOrder();
    }, [isPlacingOrder, enableSteppedModal]);

    const handleOrderModalFinish = (cartId: string) => {
        setIsOrderModalOpen(false);
        setPendingOrderNumber(null);
        setPendingOrderCartId(null);
        if (cartId && confirmationPageLink) {
            history.push(`${confirmationPageLink.url}?cartId=${cartId}`);
        }
    };

    const handleOrderModalError = () => {
        setIsOrderModalOpen(false);
        setIsCardNumberTokenized(false);
        setIsPlacingOrder({ isPlacingOrder: false });
        toasterContext.addToast({
            body: translate("An error occurred while processing your order. Please try again."),
            messageType: "danger",
            isFocusAfterMount: true,
        });
    };


    useEffect(() => {

        const checkValidation = async () => {
            const isValid = await validateForm();
            if (!isValid) {
                setShowFormErrors(true);
            }
        };
        if(enableSteppedModal===false)
        {
            checkValidation();
        }
    }, [cart]);

    useEffect(() => {
        const checkValidation = async () => {
            const isValid = await validateForm();
            if (!isValid) {
                setShowFormErrors(true);
            }
        };
        checkValidation();
        if (isCardNumberTokenized || isECheckTokenized) {
            setPaymentDetailsState({ paymentDetailsState: "complete" });
        }
    }, [isCardNumberTokenized, isECheckTokenized]);

    useEffect(() => {
        if (!cartState.isLoading && cartState.value && cartState.value.paymentMethod && paymentMethod === "") {
            setPaymentMethod(cartState.value.paymentMethod.name);
        }
        if (!cartState.isLoading && cartState.value && cartState.value.status === "EmergencyCart") {
            setIsEmergencyOrder(true);
        }
    }, [cartState]);

    let tokenName: string | undefined;
    const eCheckDetails = useRef<Validatable>(null);
    const creditCardDetails = useRef<CreditCardDetailsValidation>(null);
    const savedPaymentProfile = useRef<SavedPaymentProfileValidation>(null);

    if (useTokenExGateway) {
        if (isPaymentProfile) {
            tokenName = paymentMethodDto!.name;
        } else if (isCreditCard) {
            tokenName = "";
        }
    }

    if (useECheckTokenExGateway) {
        if (isECheck) {
            tokenName = "";
        }
    }

    useEffect(() => {
        if (typeof tokenName !== "undefined") {
            loadTokenExConfig({ token: tokenName, isECheck });
        }

        if (useSpreedlyDropIn && !spreedlyConfig?.environmentKey) {
            loadSpreedlyConfig();
        }
    }, [paymentMethod]);

    const handleTokenExSessionExpired = (token: string) => {
        loadTokenExConfig({ token, isECheck });
    };

    const tokenExConfig = typeof tokenName !== "undefined" ? tokenExConfigs[tokenName] : undefined;

    const saveCreditCardChangeHandler = (event: React.SyntheticEvent, value: boolean) => {
        handleSaveCardChange(value);
    };

    useEffect(() => {
        if (placeOrderErrorMessage === siteMessage("CreditCardInfo_MaximumCreditCardAttempts")) {
            toasterContext.addToast({
                body: placeOrderErrorMessage,
                messageType: "danger",
            });

            setTimeout(() => {
                signOut();
                signInPageLink && history.push(signInPageLink.url);
            }, 3000);
        }
    }, [placeOrderErrorMessage]);

    const handleAccountNumberIFrame = (accountNumberIFrame: IFrame) => {
        tokenExAccountNumberIframe = accountNumberIFrame;
    };

    const validateForm = async () => {
        const paymentMethodValid = validatePaymentMethod(paymentMethod);
        let poNumberValid = validatePONumber(poNumber);

        if (useECheckTokenExGateway && isECheck) {
            tokenExAccountNumberIframe?.validate();
        }
        let accountHolderNameValid = true;
        let accountNumberValid = true;
        let routingNumberValid = true;
        if (eCheckDetails?.current) {
            accountHolderNameValid = eCheckDetails.current.validateAccountHolderNameChange(accountHolderName);
            accountNumberValid = eCheckDetails.current.validateAccountNumberChange(accountNumber);
            routingNumberValid = eCheckDetails.current.validateRoutingNumberChange(routingNumber);
        }
        let creditCardValid = true;
        if (creditCardDetails?.current) {
            creditCardValid = creditCardDetails.current.validate();
        }

        if (savedPaymentProfile?.current) {
            creditCardValid = savedPaymentProfile?.current.validate();
        }      

        // Check for duplicate PO number - always call for NA region when placing order
        if (isNA && poNumber?.trim()) {
            const isDuplicate = await validatePONumberDuplicate(poNumber);
            if (isDuplicate) {
                poNumberRef.current?.focus();
                poNumberValid = true;
            }
        }       

        if (!paymentMethodValid) {
            paymentMethodRef.current?.focus();
        } else if (!poNumberValid) {
            poNumberRef.current?.focus();
        }

        return (
            paymentMethodValid &&
            poNumberValid &&
            creditCardValid &&
            accountHolderNameValid &&
            accountNumberValid &&
            routingNumberValid
        );
    };

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const isValid = await validateForm();
        if (!isValid) {
            setShowFormErrors(true);
            return false;
        }

        if (useTokenExGateway && ((isPaymentProfile && !bypassCvvForSavedCards) || isCreditCard)) {
            creditCardDetails.current?.tokenize();
            savedPaymentProfile.current?.tokenize();
        } else if (useECheckTokenExGateway && isECheck) {
            tokenExAccountNumberIframe?.tokenize();
        } else if (useSpreedlyDropIn && isCreditCard) {
            creditCardDetails.current?.tokenize();
        } else {
            setPaymentDetailsState({ paymentDetailsState: "complete" });
        }

        return false;
    };

    if (!tokenExFrameStyleConfig) {
        tokenExFrameStyleConfig = generateTokenExFrameStyleConfig({ theme });
    }

    if (!spreedlyFrameStyleConfig && useSpreedlyDropIn) {
        spreedlyFrameStyleConfig = generateSpreedlyFrameStyleConfig({ theme });
    }

    if (
        !cart ||
        cart.requiresApproval ||
        !paymentOptions ||
        (useTokenExGateway && !tokenExLibraryIsLoaded) ||
        (useSpreedlyDropIn && !spreedlyLibraryIsLoaded)
    ) {
        return null;
    }

    const handleChangedShipComplete: CheckboxProps["onChange"] = (_, value) => {
        setShipComplete({ shipComplete: value });
        setDanaShipComplete(value);
    };

    const iframeName = useTokenExGateway ? "TokenEx" : useSpreedlyDropIn ? "Spreedly" : undefined;

    return (
        <>
            <GridContainer {...styles.paymentDetailsGridContainer}>
                <GridItem {...styles.paymentDetailsHeadingGridItem}>
                    <Typography {...styles.paymentDetailsHeadingText} as="h2" data-test-selector="paymentDetailHeader">
                        {translate("Purchase Order Number")}
                    </Typography>
                </GridItem>
                {paymentDetailsState === "complete" && (
                    <GridItem {...styles.editGridItem}>
                        <Link
                            {...styles.editLink}
                            onClick={() => {
                                setIsCardNumberTokenized(false);
                                setPaymentDetailsState({
                                    paymentDetailsState: "active",
                                });
                            }}
                        >
                            {translate("Edit")}
                        </Link>
                    </GridItem>
                )}
                {paymentDetailsState === "complete" && (
                    <GridItem {...styles.paymentdMethodInfoGridItem}>
                        {promotionsWithCode.length > 0 && (
                            <>
                                <Typography {...styles.discountLabel}>{translate("Discount")}:</Typography>
                                {promotionsWithCode.map(promotion => (
                                    <Typography
                                        as="p"
                                        key={`promotion-with-code-${promotion.id}`}
                                        {...styles.appliedPromoCodeOverviewText}
                                    >{`${promotion.promotionCode} ${promotion.name}`}</Typography>
                                ))}
                            </>
                        )}
                        <Typography as="p" {...styles.paymentdMethodNameText} data-test-selector="paymentMethodName">
                            {paymentMethodDto?.description || paymentMethodDto?.name}
                        </Typography>
                        {paymentMethodDto?.isCreditCard && (
                            <Typography
                                as="p"
                                {...styles.paymentdMethodCreditCardInfoText}
                                data-test-selector="paymentMethodCreditCardInfoText"
                            >
                                {cardType}&nbsp;
                                {`****${cardNumber.slice(-4)}`}&nbsp;
                                {`0${expirationMonth}`.slice(-2)}/{`${expirationYear}`.slice(-2)}
                            </Typography>
                        )}
                    </GridItem>
                )}
                {/* {paymentDetailsState === "active" && ( */}
                <GridItem {...styles.paymentdMethodInfoGridItem}>
                    <StyledForm
                        {...styles.form}
                        id="reviewAndSubmitPaymentForm"
                        onSubmit={handleFormSubmit}
                        noValidate={true}
                        data-test-selector="reviewAndSubmitPaymentForm"
                    >
                        <StyledFieldSet {...styles.fieldset}>
                            <GridContainer {...styles.paymentMethodAndPONumberContainer}>

                                {cart.showPoNumber && (
                                    <> 
                                        <GridItem {...styles.poNumberGridItem}>
                                            <TextField
                                                {...styles.poNumberText}
                                                label={
                                                    <>
                                                        <span aria-hidden>{translate("Primary PO Number")}</span>
                                                        <VisuallyHidden>
                                                            {translate("Primary PO Number")}
                                                        </VisuallyHidden>
                                                    </>
                                                }
                                                value={poNumber}
                                                onChange={handlePONumberChange}
                                                onBlur={isNA?handlePONumberBlurChange:undefined}
                                                required={cart.requiresPoNumber}
                                                maxLength={50}
                                                error={showFormErrors && poNumberError}
                                                data-test-selector="onePageCheckout_poNumber"
                                                ref={poNumberRef}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.vatNumberGridItem}>
                                            <TextField
                                                {...styles.vatNumberText}
                                                label={translate("Secondary PO No. (Optional)")}
                                                value={vatNumber}
                                                onChange={handleVatNumberChange}
                                                maxLength={50}
                                                data-test-selector="onePageCheckout_optionalpoNumber"
                                            />
                                        </GridItem>
                                        {isNA && (
                                            <>
                                                <GridItem {...styles.vatNumberGridItem}>
                                                    <Checkbox
                                                        checked={danaShipComplete}
                                                        onChange={handleChangedShipComplete}
                                                    >{translate("Ship Complete")}
                                                    </Checkbox>
                                                </GridItem>
                                            </>
                                        )}

                                    </>

                                )}

                            </GridContainer>
                        </StyledFieldSet>
                    </StyledForm>
                </GridItem>

            </GridContainer>

            {enableSteppedModal && (
                <OrderProcessSteps
                    isOpen={isOrderModalOpen}
                    orderNumber={pendingOrderNumber}
                    orderCartId={pendingOrderCartId}
                    isBatchSubmit={pendingIsBatchSubmit}
                    originalCartLines={originalCartLinesRef.current}
                    originalOrderSubTotal={originalOrderSubTotalRef.current}
                    onFinish={handleOrderModalFinish}
                    onError={handleOrderModalError}
                    extendedStyles={styles.priceChangeModal}
                    isEmergencyOrder={isEmergencyOrder}
                />
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withToaster(withHistory(withTheme(OnePageCheckoutPaymentDetails)))),
    definition: {
        displayName: "Payment Details",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
